import { type Control, Controller } from 'react-hook-form'
import type { ReactElement, ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Col, Form } from 'react-bootstrap'
import styles from '../assets/scss/FormControlTextArea.module.scss'

export interface FormControlTextAreaProps {
  label?: string
  placeholder?: string
  value: string
  name: string
  required: boolean
  maxLength: number
  rows: number
  control: Control
  feedback?: ReactNode
  disabled?: boolean
}

function FormControlTextArea ({
  label,
  placeholder,
  value,
  name,
  required,
  maxLength,
  rows,
  control,
  feedback,
  disabled
}: Readonly<FormControlTextAreaProps>): ReactElement {
  const { t } = useTranslation()

  return (
    <Form.Group
      as={Col}
      controlId={name}
      className={styles['form-control-text-area']}
    >
      {label && (
        <Form.Label className={required ? 'required' : undefined}>
          {label}
        </Form.Label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          validate: valueToValidate =>
            valueToValidate?.trim().length > maxLength
              ? t('validation.error.form_control_text_area.max_length', {
                  count: maxLength
                })
              : true
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <Form.Control
              name={name}
              as="textarea"
              rows={rows}
              required={required}
              disabled={disabled}
              value={value}
              placeholder={placeholder}
              onChange={field.onChange}
            />
            <Form.Control.Feedback
              type={error ? 'invalid' : 'valid'}
              className={styles.feedback}
            >
              <span>
                <Trans
                  i18nKey="common.form_control_text_area.counter"
                  values={{
                    count: value.trim().length,
                    limit: maxLength
                  }}
                />
              </span>
              {(error && (
                <span>
                  {t(error.message as string, { defaultValue: error.message })}
                </span>
              )) ||
                feedback}
            </Form.Control.Feedback>
          </>
        )}
      />
    </Form.Group>
  )
}

export default FormControlTextArea
